import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import type React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H6Regular,
    MonoBodySmallRegular,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { billReviewSourceAtom } from '../../bills/state/BillsAtoms';
import { useDeleteSpinwheelUser } from '../../onboarding/hooks/useDeleteSpinwheelUser';
import { useUser } from '../../user/providers/UserProvider';

export const ProductTesting = () => {
    const deleteSpinwheelUser = useDeleteSpinwheelUser();
    const { user, loading: isUserLoading, refetch: refetchUser } = useUser();
    const toast = useToastController();
    const setBillReviewSource = useSetAtom(billReviewSourceAtom);

    // Statsig feature gate - only show for internal testing
    const { value: shouldShowTesting } = useFeatureGate(
        'general_product_testing',
    );

    if (!shouldShowTesting) {
        return null;
    }

    const userHasSpinwheel =
        user?.spinwheelId !== undefined && user?.spinwheelId !== null;

    return (
        <YStack gap={12} marginTop={24}>
            <H6Regular color="$gray1000">🧪 Product Testing</H6Regular>
            <XStack>
                <BodySmallRegular padding={1}>Logged in as </BodySmallRegular>
                <MonoBodySmallRegular
                    color="$white"
                    backgroundColor="$dropletBlue800"
                    padding={1}
                >
                    {user?.email ?? 'unknown'}
                </MonoBodySmallRegular>
            </XStack>
            <YStack
                backgroundColor="$orange100"
                borderRadius={16}
                padding={16}
                borderWidth={2}
                borderColor="$orange300"
                gap={8}
            >
                <YStack alignItems="center" gap={4}>
                    <BodyMediumRegular color="$orange1000" fontWeight="600">
                        🎯 Spinwheel Testing
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$orange900" fontSize={12}>
                        Internal testing tools
                    </BodyMediumRegular>
                </YStack>

                <YStack gap={6}>
                    <Button
                        text="🎲 Start Spinwheel"
                        variant={ButtonVariant.SECONDARY}
                        disabled={isUserLoading || userHasSpinwheel}
                        size={ButtonSize.SMALL}
                        onPress={() => {
                            setBillReviewSource('spinwheel-testing');
                            router.push('/onboarding/expenses/disclaimer');
                        }}
                        buttonProps={{
                            backgroundColor: '$orange200',
                            borderColor: '$orange400',
                        }}
                    />
                    <Button
                        text="✅ View new bills page"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        disabled={isUserLoading || !userHasSpinwheel}
                        onPress={() => {
                            setBillReviewSource('spinwheel-testing');
                            router.push({
                                pathname:
                                    '/onboarding/expenses/bills-processing',
                            });
                        }}
                        buttonProps={{
                            backgroundColor: '$green200',
                            borderColor: '$green400',
                        }}
                    />
                    <Button
                        text="🗑️ Reset SpinwheelUser"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        disabled={
                            isUserLoading ||
                            !userHasSpinwheel ||
                            deleteSpinwheelUser.isPending
                        }
                        onPress={() => {
                            deleteSpinwheelUser.mutate(
                                {},
                                {
                                    onSuccess: async () => {
                                        console.log(
                                            'Spinwheel user deleted successfully',
                                        );
                                        toast.show(
                                            'Spinwheel user deleted successfully',
                                        );

                                        // Refetch user to update spinwheelId to null
                                        if (refetchUser) {
                                            await refetchUser();
                                        }
                                    },
                                    onError: (error) => {
                                        console.error(
                                            'Failed to delete Spinwheel user:',
                                            error,
                                        );
                                        toast.show(
                                            'Failed to delete Spinwheel user',
                                        );
                                    },
                                },
                            );
                        }}
                        buttonProps={{
                            backgroundColor: '$red200',
                            borderColor: '$red400',
                        }}
                    />
                </YStack>
            </YStack>
        </YStack>
    );
};
